import MarketGroupComparison from './MarketGroupComparison';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import { Button, Callout, EmptyState, FormSelect } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { useAppQuery } from '@/hooks/useAppQuery';
import CopySummaryButton from './CopySummaryButton';
import ReportViewControls from './ReportViewControls';
import RunScoreboard from './RunScoreboard';
import {
    describeRunExposure,
    describeRunLenses,
    runnableVariants,
    type IdeaLabRunSummary,
    type IdeaLabVariantSummary
} from '../ideaLabUtils';
import {
    buildRunReportPath,
    buildScoreboard,
    buildScoreboardMarkdown,
    describeReportSlice,
    resolveReportView,
    type ReportView,
    type ReportViewRequest
} from '../reportView';
import { describeRunWindowShort, describeVariantCount, formatWallClockShort, pickDefaultResultsRun } from '../studyView';

interface StudyResultsPanelProps {
    /** Completed runs, newest first. */
    completedRuns: IdeaLabRunSummary[];
    variants: IdeaLabVariantSummary[];
    selectedRunId: number | null;
    onSelectRun: (runId: number) => void;
    /** Whether any run is still queued or evaluating. */
    hasActiveRun: boolean;
    /** Loads the report only while the Results tab is showing. */
    isActive: boolean;
    canQueueRun: boolean;
    onNewRun: () => void;
}

/**
 * The study's answer at a glance: one completed run's scoreboard. Variants are ranked within a single
 * run so every row shares the same symbols, window, and horizons; switching runs switches the field.
 */
const StudyResultsPanel = ({
    completedRuns,
    variants,
    selectedRunId,
    onSelectRun,
    hasActiveRun,
    isActive,
    canQueueRun,
    onNewRun
}: StudyResultsPanelProps) => {
    const navigate = useNavigate();
    const run = completedRuns.find((candidate) => candidate.id === selectedRunId) ?? pickDefaultResultsRun(completedRuns);
    const [viewRequest, setViewRequest] = useState<ReportViewRequest>({});
    const reportQuery = useAppQuery(
        {
            ...trpc.ideaLab.getRunResults.queryOptions({ runId: run?.id ?? 0 }),
            enabled: isActive && run !== null,
            staleTime: Infinity
        },
        { fallbackErrorMessage: 'Failed to load the run results' }
    );
    const report = reportQuery.data ?? null;
    const view = useMemo(() => (report ? resolveReportView(report, viewRequest) : null), [report, viewRequest]);
    const missingVariantCount = useMemo(() => {
        if (!report) return 0;

        const inRun = new Set(report.variants.map((variant) => variant.variantId));

        return runnableVariants(variants).filter((variant) => !inRun.has(variant.id)).length;
    }, [report, variants]);

    if (run === null) {
        return (
            <EmptyState
                action={
                    canQueueRun && !hasActiveRun ? (
                        <Button variant="primary" onClick={onNewRun}>
                            Queue a run
                        </Button>
                    ) : undefined
                }
                description={
                    hasActiveRun
                        ? 'A run is in progress. Its scoreboard appears here as soon as it completes.'
                        : 'Freeze at least one variant, then queue a run to compare variants against the control, SPY, and random entries.'
                }
                size="sm"
                title="No results yet"
            />
        );
    }

    const updateView = (patch: Partial<ReportView>) => setViewRequest((current) => ({ ...current, ...view, ...patch }));

    return (
        <div className="space-y-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div className="min-w-0 max-w-3xl flex-1">
                    <FormSelect
                        label="Results from run"
                        noMargin
                        options={completedRuns.map((candidate) => ({
                            value: candidate.id,
                            label: `#${candidate.id} · ${candidate.label} · ${describeVariantCount(candidate.variantIds.length)}`
                        }))}
                        value={run.id}
                        onChange={(event) => {
                            onSelectRun(Number(event.target.value));
                            setViewRequest({});
                        }}
                    />
                    <p className="mt-1.5 text-xs text-zinc-500">
                        {run.primaryDuration} bars · {describeRunWindowShort(run)} · {describeRunLenses(run)} · completed{' '}
                        {formatWallClockShort(run.completedAt)}
                    </p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                    {report && view && (
                        <CopySummaryButton
                            buildText={() =>
                                buildScoreboardMarkdown({
                                    title: `Idea Lab run #${run.id}: ${run.label}`,
                                    facts: [
                                        `${run.primaryDuration} bars`,
                                        describeRunWindowShort(run),
                                        describeRunLenses(run),
                                        describeVariantCount(run.variantIds.length)
                                    ],
                                    sliceLabel: describeReportSlice(view),
                                    scoreboard: buildScoreboard(report, view),
                                    url: `${window.location.origin}${buildRunReportPath(run.id, view)}`,
                                    exposureNote: describeRunExposure(report.exposure, run).body
                                })
                            }
                        />
                    )}
                    <Button size="sm" to={buildRunReportPath(run.id, view ?? undefined)} variant="primary">
                        Open full report
                    </Button>
                </div>
            </div>

            {reportQuery.isLoading ? (
                <DataLoadingSpinner text="Loading results..." />
            ) : reportQuery.error instanceof Error ? (
                <Callout variant="error">{reportQuery.error.message}</Callout>
            ) : report && view ? (
                <>
                    <MarketGroupComparison report={report} view={view} />
                    <ReportViewControls report={report} view={view} onChange={updateView} />
                    <RunScoreboard
                        report={report}
                        rowActionLabel="Open in the run report"
                        view={view}
                        onRowClick={(variantId) => void navigate(buildRunReportPath(run.id, { ...view, variantId }))}
                    />
                    <div className="space-y-1 text-xs leading-relaxed text-zinc-500">
                        <p>
                            Variants are compared within one run, so every row shares the same symbols, window, and horizons. Green outlines
                            mark the best value in each column (returns and edges only when positive). Click a row to open its full detail.
                            {missingVariantCount > 0 &&
                                ` ${missingVariantCount} frozen ${missingVariantCount === 1 ? 'variant is' : 'variants are'} not in this run; pick another run to see ${missingVariantCount === 1 ? 'it' : 'them'}.`}
                        </p>
                        <p className="text-amber-300/80">{describeRunExposure(report.exposure, run).body}</p>
                    </div>
                </>
            ) : null}
        </div>
    );
};

export default StudyResultsPanel;
